<div class="me-dialog">
  <h2 mat-dialog-title>Mapping Configuration — {{ data.dataSetName }}</h2>

  <mat-dialog-content>
    <div *ngIf="loading" class="me-loading">Loading…</div>
    <div *ngIf="error" class="me-error">{{ error }}</div>

    <form *ngIf="!loading" [formGroup]="form">

      <div class="me-section-header">
        <strong>Table Mappings</strong>
        <button type="button" mat-button (click)="addTableMapping()">+ Add Table Mapping</button>
      </div>

      <div formArrayName="tableMappings">
        <mat-accordion multi="true">
          <mat-expansion-panel *ngFor="let tm of tableMappings.controls; let i = index"
               [formGroupName]="i"
               class="me-table-card"
               [expanded]="expandedFlags[i]"
               (opened)="expandedFlags[i] = true"
               (closed)="expandedFlags[i] = false">

            <mat-expansion-panel-header>
              <mat-panel-title [title]="tableMappingSummary(tm)">{{ tableMappingSummary(tm) }}</mat-panel-title>
              <mat-panel-description>
                <span>{{ fieldConversionsOf(tm).length }} field mapping{{ fieldConversionsOf(tm).length === 1 ? '' : 's' }}</span>
                <button type="button" mat-icon-button color="warn" (click)="removeTableMapping(i); $event.stopPropagation()" aria-label="Remove table mapping">&times;</button>
              </mat-panel-description>
            </mat-expansion-panel-header>

          <ng-template matExpansionPanelContent>
          <div class="me-tm-fields">
            <div class="me-tm-field">
              <mat-form-field appearance="outline" class="me-field-full" floatLabel="always">
                <mat-label>From Table (source)</mat-label>
                <mat-select placeholder="Select From Table (source)" formControlName="fromTable">
                  <mat-option *ngFor="let t of sourceTables" [value]="tableKey(t)">{{ t.displayName }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <div class="me-tm-field">
              <mat-form-field appearance="outline" class="me-field-full" floatLabel="always">
                <mat-label>To Table (WellChron)</mat-label>
                <mat-select placeholder="Select To Table (WellChron)" formControlName="toTable">
                  <mat-option *ngFor="let t of targetTables" [value]="tableKey(t)">{{ t.displayName }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <div class="me-tm-field">
              <mat-checkbox formControlName="distinct">Distinct</mat-checkbox>
            </div>

            <div class="me-tm-field">
              <div class="me-tm-field-header" (click)="tm.get('conditionExpanded')?.setValue(!tm.get('conditionExpanded')?.value)">
                <span>Condition<span class="me-tm-field-hint" *ngIf="tm.get('conditionEnabled')?.value"> (enabled)</span></span>
                <span>{{ tm.get('conditionExpanded')?.value ? '▴' : '▾' }}</span>
              </div>

              <div class="me-tm-field-body" *ngIf="tm.get('conditionExpanded')?.value">
                <mat-checkbox formControlName="conditionEnabled"
                              (change)="onConditionEnabledChange(tm, $event.checked)">Enabled</mat-checkbox>

                <ng-container *ngIf="tm.get('conditionEnabled')?.value">
                  <div *ngIf="tm.get('conditionUnsupported')?.value" class="me-empty">
                    This table has a complex condition that isn't editable here yet — it will be kept as-is when you save.
                  </div>

                  <ng-container *ngIf="!tm.get('conditionUnsupported')?.value">
                  <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="conditionRulesOf(tm).length > 1" floatLabel="always">
                    <mat-label>Match</mat-label>
                    <mat-select placeholder="Select Match" formControlName="conditionLogic">
                      <mat-option value="and">All (AND)</mat-option>
                      <mat-option value="or">Any (OR)</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <div formArrayName="conditionRules" class="me-more-rules">
                    <div *ngFor="let rule of conditionRulesOf(tm).controls; let k = index" [formGroupName]="k" class="me-condition-rule">
                      <div class="me-row">
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Left</mat-label>
                          <mat-select placeholder="Select Left" formControlName="leftMode">
                            <mat-option value="field">Field</mat-option>
                            <mat-option value="expression">Expression</mat-option>
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('leftMode')?.value === 'field'" floatLabel="always">
                          <mat-label>Field</mat-label>
                          <mat-select placeholder="Select Field" formControlName="left">
                            <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Operator</mat-label>
                          <mat-select placeholder="Select Operator" formControlName="op">
                            <mat-option value="eq">=</mat-option>
                            <mat-option value="neq">≠</mat-option>
                            <mat-option value="gt">&gt;</mat-option>
                            <mat-option value="lt">&lt;</mat-option>
                            <mat-option value="gteq">&gt;=</mat-option>
                            <mat-option value="lteq">&lt;=</mat-option>
                            <mat-option value="like">LIKE</mat-option>
                          </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Right</mat-label>
                          <mat-select placeholder="Select Right" formControlName="rightMode">
                            <mat-option value="field">Value</mat-option>
                            <mat-option value="expression">Expression</mat-option>
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('rightMode')?.value === 'field'" floatLabel="always">
                          <mat-label>Value</mat-label>
                          <input matInput placeholder="Enter Value" formControlName="right" />
                        </mat-form-field>
                        <mat-checkbox formControlName="isConstant">Literal</mat-checkbox>

                        <ng-container *ngIf="rule.get('rightMode')?.value === 'field'">
                          <button type="button" mat-button
                                  (click)="rule.get('op')?.setValue('eq'); rule.get('right')?.setValue('NULL_VALUE')">Is Null</button>
                          <button type="button" mat-button
                                  (click)="rule.get('op')?.setValue('neq'); rule.get('right')?.setValue('NULL_VALUE')">Is Not Null</button>
                        </ng-container>

                        <button type="button" mat-icon-button color="warn" (click)="removeConditionRule(tm, k)" aria-label="Remove condition">&times;</button>
                      </div>

                      <div class="me-expr-editor" *ngIf="rule.get('leftMode')?.value === 'expression'" [formGroupName]="'leftField'">
                        <span class="me-more-group-label">Left Expression</span>
                        <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!rule.get('leftField.isConstant')?.value" floatLabel="always">
                          <mat-label>From Field</mat-label>
                          <mat-select placeholder="Select From Field" formControlName="fromFieldOrConstant">
                            <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('leftField.isConstant')?.value" floatLabel="always">
                          <mat-label>Constant Value</mat-label>
                          <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Schema</mat-label>
                          <input matInput placeholder="Enter Link Schema" formControlName="linkSchema" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Table</mat-label>
                          <input matInput placeholder="Enter Link Table" formControlName="linkTable" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Alias</mat-label>
                          <input matInput placeholder="Enter Link Alias" formControlName="linkAlias" />
                        </mat-form-field>
                      </div>

                      <div class="me-expr-editor" *ngIf="rule.get('rightMode')?.value === 'expression'" [formGroupName]="'rightField'">
                        <span class="me-more-group-label">Right Expression</span>
                        <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!rule.get('rightField.isConstant')?.value" floatLabel="always">
                          <mat-label>From Field</mat-label>
                          <mat-select placeholder="Select From Field" formControlName="fromFieldOrConstant">
                            <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('rightField.isConstant')?.value" floatLabel="always">
                          <mat-label>Constant Value</mat-label>
                          <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Schema</mat-label>
                          <input matInput placeholder="Enter Link Schema" formControlName="linkSchema" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Table</mat-label>
                          <input matInput placeholder="Enter Link Table" formControlName="linkTable" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Link Alias</mat-label>
                          <input matInput placeholder="Enter Link Alias" formControlName="linkAlias" />
                        </mat-form-field>
                      </div>
                    </div>
                  </div>

                  <button type="button" mat-button (click)="addConditionRule(tm)">+ Add Condition</button>
                </ng-container>
              </ng-container>
            </div>
          </div>

          <ng-template #fieldMappingEditor let-mappings="mappings" let-mode="mode">
              <mat-accordion multi="true">
                <mat-expansion-panel *ngFor="let fc of mappings.controls; let j = index"
                                     [formGroup]="$any(fc)"
                                     class="me-fc-card"
                                     [expanded]="fc.get('expanded')?.value"
                                     (opened)="fc.get('expanded')?.setValue(true)"
                                     (closed)="fc.get('expanded')?.setValue(false)">

                  <mat-expansion-panel-header>
                    <mat-panel-title [title]="fieldMappingSummary(tm, fc)">{{ fieldMappingSummary(tm, fc) }}</mat-panel-title>
                    <mat-panel-description>
                      <button type="button" mat-icon-button color="warn" (click)="removeMapping(mappings, j); $event.stopPropagation()" aria-label="Remove field mapping">&times;</button>
                    </mat-panel-description>
                  </mat-expansion-panel-header>

                  <div class="me-fc-row">
                    <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                      <mat-label>To Field</mat-label>
                      <mat-select placeholder="Select a Field" formControlName="toField">
                        <mat-option *ngFor="let f of targetFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                      </mat-select>
                    </mat-form-field>
                    <span class="me-unit-hint" *ngIf="targetFieldUnit(tm, fc)">{{ targetFieldUnit(tm, fc) }}</span>

                    <mat-checkbox formControlName="isConstant">Constant  </mat-checkbox>

                    <mat-form-field appearance="outline" class="me-field" *ngIf="!fc.get('isConstant')?.value" floatLabel="always">
                      <mat-label>From Field</mat-label>
                      <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                        <mat-option *ngFor="let f of fieldSourceOptionsFor(tm, fc)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                      </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="me-field" *ngIf="fc.get('isConstant')?.value" floatLabel="always">
                      <mat-label>Constant Value</mat-label>
                      <input matInput placeholder="Enter a Value" formControlName="fromFieldOrConstant" />
                    </mat-form-field>
                  </div>

                  <div class="me-fc-more">
                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'unitMath')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'unitMath')">Unit / Math</span>
                        <span>{{ isSectionExpanded(fc, 'unitMath') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'unitMath')">
                        <mat-form-field appearance="outline" class="me-field me-field--sm"  floatLabel="always">
                          <mat-label>Source Unit</mat-label>
                          <input matInput formControlName="sourceUnit" placeholder="e.g. ft" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm"  floatLabel="always">
                          <mat-label>Divide By</mat-label>
                          <input matInput type="number" formControlName="divide" placeholder="1"/>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm"  floatLabel="always">
                          <mat-label>Multiply By</mat-label>
                          <input matInput type="number" formControlName="multiply" placeholder="1"/>
                        </mat-form-field>
                        <mat-checkbox formControlName="inverse">Inverse</mat-checkbox>
                        <mat-checkbox formControlName="isFraction">Is Fraction</mat-checkbox>
                        <mat-checkbox formControlName="ignoreUnits">Ignore Units</mat-checkbox>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'text')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'text')">Text</span>
                        <span>{{ isSectionExpanded(fc, 'text') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'text')">
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Prefix</mat-label>
                          <input matInput formControlName="prefix" placeholder="enter a prefix" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Suffix</mat-label>
                          <input matInput formControlName="suffix" placeholder="enter a suffix" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Max Chars</mat-label>
                          <input matInput type="number" formControlName="maxChars" placeholder="1" />
                        </mat-form-field>
                      </div>
                    </div>

                    <div class="me-more-group">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'link')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'link')">Link to another table</span>
                        <span>{{ isSectionExpanded(fc, 'link') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'link')">
                        <div formArrayName="linkChain" class="me-more-rules">
                          <div *ngFor="let hop of linkChainOf(fc).controls; let h = index" [formGroupName]="h" class="me-link-hop">
                            <span class="me-tm-field-hint" *ngIf="h > 0">then join to</span>
                            <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                              <mat-label>Table</mat-label>
                              <mat-select placeholder="Select Table" formControlName="table">
                                <mat-option *ngFor="let t of sourceTables" [value]="tableKey(t)">{{ t.displayName }}</mat-option>
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                              <mat-label>Alias</mat-label>
                              <input matInput formControlName="alias" placeholder="Enter an Alias"/>
                            </mat-form-field>
                            <button type="button" mat-icon-button color="warn" (click)="removeLinkHop(fc, h)" aria-label="Remove link hop">&times;</button>
                          </div>
                          <div *ngIf="linkChainOf(fc).length === 0" class="me-empty">Not linked to another table.</div>
                        </div>
                        <button type="button" mat-button (click)="addLinkHop(fc)">{{ linkChainOf(fc).length === 0 ? '+ Add Link' : '+ Chain Another Join' }}</button>
                        <mat-checkbox formControlName="blindJoin">Blind Join</mat-checkbox>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'childAggregate')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'childAggregate')">Aggregate from child table</span>
                        <span>{{ isSectionExpanded(fc, 'childAggregate') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'childAggregate')">
                        <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                          <mat-label>Child Table</mat-label>
                          <mat-select placeholder="Select Child Table" formControlName="childTable">
                            <mat-option value="">(none)</mat-option>
                            <mat-option *ngFor="let t of sourceTables" [value]="tableKey(t)">{{ t.displayName }}</mat-option>
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Child Alias</mat-label>
                          <input matInput formControlName="childAlias" placeholder="Enter an Alias"/>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Aggregate</mat-label>
                          <mat-select placeholder="Select Aggregate" formControlName="childAggregate">
                            <mat-option value="None">None</mat-option>
                            <mat-option value="First">First</mat-option>
                            <mat-option value="Max">Max</mat-option>
                            <mat-option value="Min">Min</mat-option>
                            <mat-option value="Avg">Avg</mat-option>
                            <mat-option value="Sum">Sum</mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'findReplace')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'findReplace')">Find &amp; Replace</span>
                        <span>{{ isSectionExpanded(fc, 'findReplace') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'findReplace')">
                        <div formArrayName="replace" class="me-more-rules">
                          <div *ngFor="let r of replaceOf(fc).controls; let m = index" [formGroupName]="m" class="me-row">
                            <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                              <mat-label>Find</mat-label>
                              <input matInput placeholder="Enter Find" formControlName="replace" />
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                              <mat-label>Replace with</mat-label>
                              <input matInput placeholder="Enter Replace with" formControlName="with" />
                            </mat-form-field>
                            <button type="button" mat-icon-button color="warn" (click)="removeReplaceRow(fc, m)" aria-label="Remove replace rule">&times;</button>
                          </div>
                          <div *ngIf="replaceOf(fc).length === 0" class="me-empty">No find/replace rules.</div>
                        </div>
                        <button type="button" mat-button (click)="addReplaceRow(fc)">+ Add Replace Rule</button>
                      </div>
                    </div>

                    <div class="me-more-group">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'concatenate')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'concatenate')">Concatenate (join as text)</span>
                        <span>{{ isSectionExpanded(fc, 'concatenate') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'concatenate')">
                        <div formArrayName="concatenate" class="me-more-rules">
                          <div *ngFor="let frag of fragmentsOf(fc, 'concatenate').controls; let n = index" [formGroupName]="n" class="me-row">
                            <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!frag.get('isConstant')?.value" floatLabel="always">
                              <mat-label>From Field</mat-label>
                              <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                                <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="frag.get('isConstant')?.value" floatLabel="always">
                              <mat-label>Constant Value</mat-label>
                              <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                              <mat-label>Prefix</mat-label>
                              <input matInput placeholder="Enter Prefix" formControlName="prefix" />
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                              <mat-label>Suffix</mat-label>
                              <input matInput placeholder="Enter Suffix" formControlName="suffix" />
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="fragmentsOf(fc, 'concatenate').length > 0" floatLabel="always">
                              <mat-label>Delimiter</mat-label>
                              <input matInput formControlName="concatenateDelimiter" placeholder="e.g. -" />
                            </mat-form-field>
                            <button type="button" mat-icon-button color="warn" (click)="removeFragment(fc, 'concatenate', n)" aria-label="Remove piece">&times;</button>
                          </div>
                          <div *ngIf="fragmentsOf(fc, 'concatenate').length === 0" class="me-empty">No pieces yet.</div>
                        </div>
                        
                        <button type="button" mat-button (click)="addFragment(fc, 'concatenate')">+ Add Piece</button>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'addition')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'addition')">Add Together (sum)</span>
                        <span>{{ isSectionExpanded(fc, 'addition') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'addition')">
                        <div formArrayName="addition" class="me-more-rules">
                          <div *ngFor="let frag of fragmentsOf(fc, 'addition').controls; let n = index" [formGroupName]="n" class="me-row">
                            <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!frag.get('isConstant')?.value" floatLabel="always">
                              <mat-label>From Field</mat-label>
                              <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                                <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="frag.get('isConstant')?.value" floatLabel="always">
                              <mat-label>Constant Value</mat-label>
                              <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                            </mat-form-field>
                            <button type="button" mat-icon-button color="warn" (click)="removeFragment(fc, 'addition', n)" aria-label="Remove term">&times;</button>
                          </div>
                          <div *ngIf="fragmentsOf(fc, 'addition').length === 0" class="me-empty">No terms yet.</div>
                        </div>
                        <button type="button" mat-button (click)="addFragment(fc, 'addition')">+ Add Term</button>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'switches')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'switches')">Conditional Value (Switches)</span>
                        <span>{{ isSectionExpanded(fc, 'switches') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'switches')">
                        <div formArrayName="switches" class="me-switch-list">
                          <div *ngFor="let sw of switchesOf(fc).controls; let s = index" [formGroupName]="s" class="me-switch-card">
                            <div class="me-switch-card-header">
                              <span>Rule {{ s + 1 }}</span>
                              <button type="button" mat-icon-button color="warn" (click)="removeSwitchRow(fc, s)" aria-label="Remove switch rule">&times;</button>
                            </div>

                            <div *ngIf="sw.get('unsupported')?.value" class="me-empty">Complex switch rule — kept as-is when you save.</div>

                            <ng-container *ngIf="!sw.get('unsupported')?.value">
                              <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                <mat-label>How is this rule matched?</mat-label>
                                <mat-select placeholder="Select a rule type" formControlName="mode" (selectionChange)="onSwitchModeChange(sw, $event.value)">
                                  <mat-option value="field">Simple: a field equals a value</mat-option>
                                  <mat-option value="condition">Advanced: one or more checks</mat-option>
                                </mat-select>
                              </mat-form-field>

                              <ng-container *ngIf="sw.get('mode')?.value === 'field'">
                                <div class="me-switch-sentence">
                                  <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                    <mat-label>If this field</mat-label>
                                    <mat-select placeholder="Select a field" formControlName="fromField">
                                      <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                                    </mat-select>
                                  </mat-form-field>
                                  <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                    <mat-label>Equals this value</mat-label>
                                    <input matInput placeholder="Enter a value" formControlName="valueCondition" />
                                  </mat-form-field>
                                </div>
                              </ng-container>

                              <ng-container *ngIf="sw.get('mode')?.value === 'condition'">
                                <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="conditionRulesOf(sw).length > 1" floatLabel="always">
                                  <mat-label>Checks must</mat-label>
                                  <mat-select placeholder="Select" formControlName="conditionLogic">
                                    <mat-option value="and">All pass (AND)</mat-option>
                                    <mat-option value="or">Any passes (OR)</mat-option>
                                  </mat-select>
                                </mat-form-field>

                                <div formArrayName="conditionRules" class="me-switch-list">
                                  <div *ngFor="let rule of conditionRulesOf(sw).controls; let k = index" [formGroupName]="k" class="me-switch-check">
                                    <div class="me-switch-check-header">
                                      <span>Check {{ k + 1 }}</span>
                                      <button type="button" mat-icon-button color="warn" (click)="removeConditionRule(sw, k)" aria-label="Remove check">&times;</button>
                                    </div>

                                    <div class="me-switch-sentence">
                                      <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                        <mat-label>Left side</mat-label>
                                        <mat-select placeholder="Select" formControlName="leftMode">
                                          <mat-option value="field">A field</mat-option>
                                          <mat-option value="expression">An expression</mat-option>
                                        </mat-select>
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field" *ngIf="rule.get('leftMode')?.value === 'field'" floatLabel="always">
                                        <mat-label>Field</mat-label>
                                        <mat-select placeholder="Select a field" formControlName="left">
                                          <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                                        </mat-select>
                                      </mat-form-field>

                                      <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                        <mat-label>Operator</mat-label>
                                        <mat-select placeholder="Select" formControlName="op">
                                          <mat-option value="eq">=</mat-option>
                                          <mat-option value="neq">≠</mat-option>
                                          <mat-option value="gt">&gt;</mat-option>
                                          <mat-option value="lt">&lt;</mat-option>
                                          <mat-option value="gteq">&gt;=</mat-option>
                                          <mat-option value="lteq">&lt;=</mat-option>
                                          <mat-option value="like">LIKE</mat-option>
                                        </mat-select>
                                      </mat-form-field>

                                      <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                        <mat-label>Right side</mat-label>
                                        <mat-select placeholder="Select" formControlName="rightMode">
                                          <mat-option value="field">A value</mat-option>
                                          <mat-option value="expression">An expression</mat-option>
                                        </mat-select>
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field" *ngIf="rule.get('rightMode')?.value === 'field'" floatLabel="always">
                                        <mat-label>Value</mat-label>
                                        <input matInput placeholder="Enter a value" formControlName="right" />
                                      </mat-form-field>
                                    </div>

                                    <div class="me-switch-check-actions">
                                      <mat-checkbox formControlName="isConstant">Literal</mat-checkbox>
                                      <ng-container *ngIf="rule.get('rightMode')?.value === 'field'">
                                        <button type="button" mat-button
                                                (click)="rule.get('op')?.setValue('eq'); rule.get('right')?.setValue('NULL_VALUE')">
                                          Is Null
                                        </button>
                                        <button type="button" mat-button
                                                (click)="rule.get('op')?.setValue('neq'); rule.get('right')?.setValue('NULL_VALUE')">
                                          Is Not Null
                                        </button>
                                      </ng-container>
                                    </div>

                                    <div class="me-expr-editor" *ngIf="rule.get('leftMode')?.value === 'expression'" [formGroupName]="'leftField'">
                                      <span class="me-more-group-label">Left Expression</span>
                                      <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!rule.get('leftField.isConstant')?.value" floatLabel="always">
                                        <mat-label>From Field</mat-label>
                                        <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                                          <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                                        </mat-select>
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('leftField.isConstant')?.value" floatLabel="always">
                                        <mat-label>Constant Value</mat-label>
                                        <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Schema</mat-label>
                                        <input matInput placeholder="Enter Link Schema" formControlName="linkSchema" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Table</mat-label>
                                        <input matInput placeholder="Enter Link Table" formControlName="linkTable" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Alias</mat-label>
                                        <input matInput placeholder="Enter Link Alias" formControlName="linkAlias" />
                                      </mat-form-field>
                                    </div>

                                    <div class="me-expr-editor" *ngIf="rule.get('rightMode')?.value === 'expression'" [formGroupName]="'rightField'">
                                      <span class="me-more-group-label">Right Expression</span>
                                      <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!rule.get('rightField.isConstant')?.value" floatLabel="always">
                                        <mat-label>From Field</mat-label>
                                        <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                                          <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                                        </mat-select>
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="rule.get('rightField.isConstant')?.value" floatLabel="always">
                                        <mat-label>Constant Value</mat-label>
                                        <input matInput placeholder="Enter Constant Value" formControlName="fromFieldOrConstant" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Schema</mat-label>
                                        <input matInput placeholder="Enter Link Schema" formControlName="linkSchema" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Table</mat-label>
                                        <input matInput placeholder="Enter Link Table" formControlName="linkTable" />
                                      </mat-form-field>
                                      <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                                        <mat-label>Link Alias</mat-label>
                                        <input matInput placeholder="Enter Link Alias" formControlName="linkAlias" />
                                      </mat-form-field>
                                    </div>
                                  </div>
                                </div>
                                <button type="button" mat-button (click)="addConditionRule(sw)">+ Add check</button>
                              </ng-container>

                              <div class="me-switch-result">
                                <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                                  <mat-label>Then use this result</mat-label>
                                  <input matInput placeholder="Enter a result" formControlName="valueResult" />
                                </mat-form-field>
                                <mat-checkbox formControlName="isConstant">Result is a literal value</mat-checkbox>
                              </div>
                            </ng-container>
                          </div>
                          <div *ngIf="switchesOf(fc).length === 0" class="me-empty">No rules yet. A rule sets a value when a condition is met.</div>
                        </div>

                        <div class="me-switch-footer">
                          <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="switchesOf(fc).length > 0" floatLabel="always">
                            <mat-label>Otherwise use (optional)</mat-label>
                            <input matInput placeholder="Enter a default" formControlName="switchDefault" />
                          </mat-form-field>
                          <button type="button" mat-button (click)="addSwitchRow(fc)">+ Add rule</button>
                        </div>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'typeControl')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'typeControl')">Type Control</span>
                        <span>{{ isSectionExpanded(fc, 'typeControl') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'typeControl')">
                        <mat-checkbox formControlName="isFunction">Is Function (uses Function Fields below)</mat-checkbox>
                        <mat-checkbox formControlName="isTime">Is Time</mat-checkbox>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Format String</mat-label>
                          <input matInput placeholder="Enter String" formControlName="formatString" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Expected Result Type</mat-label>
                          <input matInput placeholder="Enter Type" formControlName="expectedResult" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Cast Type</mat-label>
                          <input matInput placeholder="Enter Type" formControlName="castType" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Default Value</mat-label>
                          <input matInput placeholder="Enter Value" formControlName="defaultValue" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                          <mat-label>Report Start Hour</mat-label>
                          <input matInput placeholder="Enter Start Hour" type="number" formControlName="reportStartHour" />
                        </mat-form-field>
                      </div>
                      <div class="me-more-group" *ngIf="mode !== 'groupBy' && fc.get('isFunction')?.value">
                        <div class="me-more-group-header" (click)="toggleSection(fc, 'functionFields')">
                          <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'functionFields')">Function Fields</span>
                          <span>{{ isSectionExpanded(fc, 'functionFields') ? '▴' : '▾' }}</span>
                        </div>
                        <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'functionFields')">
                          <div formArrayName="functionFields" class="me-more-rules">
                            <div *ngFor="let frag of fragmentsOf(fc, 'functionFields').controls; let n = index" [formGroupName]="n" class="me-row">
                              <mat-checkbox formControlName="isConstant">Constant</mat-checkbox>
                              <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="!frag.get('isConstant')?.value" floatLabel="always">
                                <mat-label>From Field</mat-label>
                                <mat-select placeholder="Select a Field" formControlName="fromFieldOrConstant">
                                  <mat-option *ngFor="let f of sourceFieldsFor(tm)" [value]="f.fieldName">{{ f.displayName }}</mat-option>
                                </mat-select>
                              </mat-form-field>
                              <mat-form-field appearance="outline" class="me-field me-field--sm" *ngIf="frag.get('isConstant')?.value" floatLabel="always">
                                <mat-label>Constant Value</mat-label>
                                <input matInput placeholder="Enter Value" formControlName="fromFieldOrConstant" />
                              </mat-form-field>
                              <button type="button" mat-icon-button color="warn" (click)="removeFragment(fc, 'functionFields', n)" aria-label="Remove function field">&times;</button>
                            </div>
                            <div *ngIf="fragmentsOf(fc, 'functionFields').length === 0" class="me-empty">No function fields yet.</div>
                          </div>
                          <button type="button" mat-button (click)="addFragment(fc, 'functionFields')">+ Add Function Field</button>
                        </div>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'cumulative')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'cumulative')">Cumulative Order</span>
                        <span>{{ isSectionExpanded(fc, 'cumulative') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'cumulative')">
                        <mat-checkbox formControlName="cumulativeEnabled">Enabled</mat-checkbox>
                        <ng-container *ngIf="fc.get('cumulativeEnabled')?.value">
                          <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                            <mat-label>Sequence Field</mat-label>
                            <input matInput placeholder="Enter Field" formControlName="cumulativeSequenceField" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                            <mat-label>Parent Schema</mat-label>
                            <input matInput placeholder="Enter Schema" formControlName="cumulativeParentSchema" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                            <mat-label>Parent Table</mat-label>
                            <input matInput placeholder="Enter Table" formControlName="cumulativeParentTable" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" class="me-field me-field--sm" floatLabel="always">
                            <mat-label>Parent Link Alias</mat-label>
                            <input matInput placeholder="Enter Alias" formControlName="cumulativeParentLinkAlias" />
                          </mat-form-field>
                        </ng-container>
                      </div>
                    </div>

                    <div class="me-more-group" *ngIf="mode !== 'groupBy'">
                      <div class="me-more-group-header" (click)="toggleSection(fc, 'sequence')">
                        <span class="me-more-group-label" [class.me-more-group-label--active]="isSectionConfigured(fc, 'sequence')">Sequence Mapping</span>
                        <span>{{ isSectionExpanded(fc, 'sequence') ? '▴' : '▾' }}</span>
                      </div>
                      <div class="me-more-group-body" *ngIf="isSectionExpanded(fc, 'sequence')">
                        <mat-checkbox formControlName="sequenceEnabled">Enabled</mat-checkbox>
                        <ng-container *ngIf="fc.get('sequenceEnabled')?.value">
                          <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                            <mat-label>Partition Fields (comma separated)</mat-label>
                            <input matInput placeholder="Enter Partition Fields" formControlName="sequencePartitionFields" />
                          </mat-form-field>
                          <mat-form-field appearance="outline" class="me-field" floatLabel="always">
                            <mat-label>Order By Fields (comma separated)</mat-label>
                            <input matInput placeholder="Enter Order By Fields" formControlName="sequenceOrderByFields" />
                          </mat-form-field>
                          <mat-checkbox formControlName="sequenceAscending">Ascending</mat-checkbox>
                        </ng-container>
                      </div>
                    </div>
                  </div>

                </mat-expansion-panel>
              </mat-accordion>
          </ng-template>
          <div class="me-tm-field">
            <div class="me-tm-field-header" (click)="tm.get('fieldMappingsExpanded')?.setValue(!tm.get('fieldMappingsExpanded')?.value)">
              <span>Field Mappings <span class="me-tm-field-hint">({{ fieldConversionsOf(tm).length }})</span></span>
              <span>{{ tm.get('fieldMappingsExpanded')?.value ? '▴' : '▾' }}</span>
            </div>

            <div class="me-tm-field-body" *ngIf="tm.get('fieldMappingsExpanded')?.value" formArrayName="fieldConversions">
              <button type="button" mat-button (click)="addFieldConversion(tm)">+ Add Field Mapping</button>
              <ng-container *ngTemplateOutlet="
                fieldMappingEditor;
                context: {
                  mappings: fieldConversionsOf(tm),
                  mode: 'field'
                }
              ">
              </ng-container>
              <div *ngIf="fieldConversionsOf(tm).length === 0" class="me-empty">No field mappings yet.</div>
            </div>
          </div>

          <div class="me-tm-field">
            <div class="me-tm-field-header" (click)="tm.get('groupByExpanded')?.setValue(!tm.get('groupByExpanded')?.value)">
              <span>Group By <span class="me-tm-field-hint">({{ groupByOf(tm).length }})</span></span>
              <span>{{ tm.get('groupByExpanded')?.value ? '▴' : '▾' }}</span>
            </div>

            <div class="me-tm-field-body" *ngIf="tm.get('groupByExpanded')?.value" formArrayName="groupBy">
              <button type="button" mat-button (click)="addGroupByRow(tm)">+ Add Group By Field</button>
                        <ng-container *ngTemplateOutlet="
                fieldMappingEditor;
                context: {
                  mappings: groupByOf(tm),
                  mode: 'groupBy'
                }
              ">
              </ng-container>
            <div *ngIf="groupByOf(tm).length === 0" class="me-empty">No group-by fields yet.</div>
            </div>
          </div>

          </div>
          </ng-template>

          </mat-expansion-panel>
        </mat-accordion>

        <div *ngIf="tableMappings.length === 0" class="me-empty">No table mappings defined.</div>
      </div>

    </form>

  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button (click)="dialogRef.close(false)">Cancel</button>
    <button mat-button color="warn" (click)="clear()">Clear Config</button>
    <button mat-flat-button color="primary" [disabled]="!hasChanges || saving" (click)="save()">
      {{ saving ? 'Saving…' : 'Save' }}
    </button>
  </mat-dialog-actions>
</div>
